import {
  closestCenter,
  CollisionDetection,
  DndContext,
  DragEndEvent,
  PointerSensor,
  UniqueIdentifier,
  useDraggable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { Fragment } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { SidePanelPropertyKey } from 'src/graphql/entities/side-panel-personalization';

export interface SortablePropertyItem {
  key: SidePanelPropertyKey;
  label: string;
  icon?: string;
  group?: { key: string; label: string };
  visible: boolean;
}

interface PropertyCluster {
  group: { key: string; label: string } | null;
  items: SortablePropertyItem[];
}

function clusterByAdjacentGroup(items: SortablePropertyItem[]): PropertyCluster[] {
  const clusters: PropertyCluster[] = [];

  for (const item of items) {
    const group = item.group ?? null;
    const current = clusters[clusters.length - 1];

    if (current && (current.group?.key ?? null) === (group?.key ?? null)) {
      current.items.push(item);
    } else {
      clusters.push({ group, items: [item] });
    }
  }

  return clusters;
}

const GroupHeader = ({ label }: { label: string }) => (
  <Stack direction="row" alignItems="center" sx={{ pl: 0.5, pr: 1.5, pt: 1, pb: 0.25 }}>
    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>
      {label}
    </Typography>
  </Stack>
);

const GROUP_DRAG_PREFIX = 'grouphdr:';

// dragging the header moves the group's whole run of rows as one block
const DraggableGroupHeader = ({
  clusterFirstKey,
  label,
  shown,
  total,
  disabled,
}: {
  clusterFirstKey: SidePanelPropertyKey;
  label: string;
  shown: number;
  total: number;
  disabled: boolean;
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `${GROUP_DRAG_PREFIX}${clusterFirstKey}`,
    disabled,
  });

  return (
    <Stack
      ref={setNodeRef}
      direction="row"
      alignItems="center"
      {...attributes}
      {...listeners}
      sx={{
        pr: 1.5,
        pt: 1,
        pb: 0.25,
        cursor: getDragCursor(!disabled, isDragging),
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.6 : 1,
        position: 'relative',
        zIndex: isDragging ? 1 : undefined,
        '& .group-drag-handle': { opacity: isDragging ? 1 : 0, transition: 'opacity 120ms' },
        '&:hover .group-drag-handle': { opacity: disabled ? 0 : 1 },
      }}
    >
      <Box className="group-drag-handle" sx={{ display: 'flex', width: '1rem' }}>
        <DynamicIcon
          name="drag_indicator"
          sx={{ color: 'text.secondary', fontSize: '1rem' }}
        />
      </Box>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ lineHeight: 1.5, flexGrow: 1 }}
        noWrap
      >
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {shown}/{total}
      </Typography>
    </Stack>
  );
};

interface SortablePropertyRowProps {
  item: SortablePropertyItem;
  disabled: boolean;
  reorderDisabled: boolean;
  onToggleVisible: () => void;
}

function getDragCursor(canDrag: boolean, isDragging: boolean): string {
  if (!canDrag) {
    return 'default';
  }
  return isDragging ? 'grabbing' : 'grab';
}

const SortablePropertyRow = ({
  item,
  disabled,
  reorderDisabled,
  onToggleVisible,
}: SortablePropertyRowProps) => {
  const canDrag = !disabled && !reorderDisabled;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable(
    { id: item.key, disabled: !canDrag },
  );

  return (
    <Stack
      ref={setNodeRef}
      direction="row"
      alignItems="center"
      gap={1}
      sx={{
        pr: 1,
        py: 0.5,
        opacity: isDragging ? 0.5 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
        // the drag handle only surfaces on hover, like the mock
        '& .row-drag-handle': { opacity: isDragging ? 1 : 0, transition: 'opacity 120ms' },
        '&:hover .row-drag-handle': { opacity: canDrag ? 1 : 0 },
      }}
    >
      <Box
        className="row-drag-handle"
        {...attributes}
        {...listeners}
        sx={{ cursor: getDragCursor(canDrag, isDragging), display: 'flex', width: '1rem' }}
      >
        <DynamicIcon
          name="drag_indicator"
          sx={{ color: 'text.secondary', fontSize: '1rem' }}
        />
      </Box>
      {item.icon ? (
        <DynamicIcon
          name={item.icon}
          sx={{
            fontSize: '1.25rem',
            color: item.visible ? 'text.secondary' : 'text.disabled',
            flexShrink: 0,
          }}
        />
      ) : (
        <Box sx={{ width: '1.25rem', flexShrink: 0 }} />
      )}
      <Typography
        variant="body2"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          color: item.visible ? 'text.primary' : 'text.secondary',
        }}
        noWrap
      >
        {item.label}
      </Typography>
      <IconButton
        size="small"
        onClick={onToggleVisible}
        disabled={disabled}
        aria-label={item.visible ? `Hide ${item.label}` : `Show ${item.label}`}
        sx={{ p: 0.25 }}
      >
        <DynamicIcon
          name={item.visible ? 'visibility' : 'visibility_off'}
          sx={{ fontSize: '1.125rem', color: item.visible ? 'text.primary' : 'text.disabled' }}
        />
      </IconButton>
    </Stack>
  );
};

interface SortablePropertyListProps {
  items: SortablePropertyItem[];
  disabled?: boolean;
  reorderDisabled?: boolean;
  onToggleVisible: (key: SidePanelPropertyKey) => void;
  onReorder: (nextKeys: SidePanelPropertyKey[]) => void;
}

export const SortablePropertyList = ({
  items,
  disabled = false,
  reorderDisabled = false,
  onToggleVisible,
  onReorder,
}: SortablePropertyListProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  if (items.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
        No properties available.
      </Typography>
    );
  }

  const keys = items.map((item) => item.key);
  const clusters = clusterByAdjacentGroup(items);
  const hasGroups = clusters.some((cluster) => cluster.group);

  const groupByKey = new Map<UniqueIdentifier, string | null>(
    items.map((item) => [item.key, item.group?.key ?? null]),
  );
  const groupOf = (id: UniqueIdentifier) => groupByKey.get(id) ?? null;

  // drags never cross regions: ungrouped rows stay in the leading run, grouped
  // rows stay in their own group, group headers target other groups — filtering
  // the droppables (vs a no-op on drop) keeps the live preview honest
  const constrainToRegion: CollisionDetection = (args) => {
    const isHeader = String(args.active.id).startsWith(GROUP_DRAG_PREFIX);
    const activeGroup = groupOf(args.active.id);

    return closestCenter({
      ...args,
      droppableContainers: args.droppableContainers.filter((container) =>
        isHeader ? groupOf(container.id) !== null : groupOf(container.id) === activeGroup,
      ),
    });
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    const activeId = String(active.id);
    // a header drag reorders whole groups: move the dragged group's block to the
    // target row's group. Clusters are already in normalized (groups-last) order,
    // so reordering them stays consistent with reconcileLayout and a drop within
    // the same group is a no-op (no spurious draft).
    if (activeId.startsWith(GROUP_DRAG_PREFIX)) {
      const firstKey = activeId.slice(GROUP_DRAG_PREFIX.length);
      const from = clusters.findIndex((cluster) => cluster.items[0].key === firstKey);
      const to = clusters.findIndex((cluster) => cluster.group?.key === groupOf(over.id));

      if (from !== -1 && to !== -1 && from !== to) {
        onReorder(arrayMove(clusters, from, to).flatMap((c) => c.items.map((i) => i.key)));
      }
      return;
    }

    const oldIndex = keys.indexOf(active.id as SidePanelPropertyKey);
    const newIndex = keys.indexOf(over.id as SidePanelPropertyKey);

    onReorder(arrayMove(keys, oldIndex, newIndex));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={constrainToRegion}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={keys} strategy={verticalListSortingStrategy}>
        {clusters.map((cluster, index) => (
          <Fragment key={cluster.items[0].key}>
            {cluster.group ? (
              <DraggableGroupHeader
                clusterFirstKey={cluster.items[0].key}
                label={cluster.group.label}
                shown={cluster.items.filter((item) => item.visible).length}
                total={cluster.items.length}
                disabled={disabled || reorderDisabled}
              />
            ) : null}
            {!cluster.group && hasGroups && index === 0 ? (
              <GroupHeader label="Properties" />
            ) : null}
            {cluster.items.map((item) => (
              <SortablePropertyRow
                key={item.key}
                item={item}
                disabled={disabled}
                reorderDisabled={reorderDisabled}
                onToggleVisible={() => onToggleVisible(item.key)}
              />
            ))}
          </Fragment>
        ))}
      </SortableContext>
    </DndContext>
  );
};
